import React from "react";
import api from "@/lib/api";
import {
  X, CheckCircle, WarningCircle, Sparkle,
  Hash, CalendarBlank, CurrencyDollar, User, Package, Receipt,
  ArrowRight, Trash, Plus, Link as LinkIcon, CaretDown, Coins,
} from "@phosphor-icons/react";

/* ═══════════════════════════════════════════════════════════════════════════
   PdfInvoiceReview — split-view review of a PDF-extracted invoice.
   Left: editable form with BU / supplier / GL / site LOV pickers.
   Right: the uploaded PDF preview.
   Props: open, sessionId, extracted, pdfUrl, onClose, onCreated
   ═══════════════════════════════════════════════════════════════════════════ */

const money = (n, c) =>
  n == null || n === "" ? "—"
    : `${c ? c + " " : ""}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ── Searchable, paginated LOV picker (BU + supplier) ─────────────────────── */
function LovPicker({ label, icon: Icon, value, onChange, endpoint, placeholder }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const listRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const reqRef = React.useRef(0);
  const PAGE = 25;

  const fetchPage = React.useCallback((search, nextOffset, append) => {
    const myReq = ++reqRef.current;
    setLoading(true);
    api.get(endpoint, { params: { search, offset: nextOffset, limit: PAGE } })
      .then(({ data }) => {
        if (myReq !== reqRef.current) return;
        const newItems = data?.items || [];
        setItems((prev) => (append ? [...prev, ...newItems] : newItems));
        setHasMore(!!data?.has_more);
        setOffset(nextOffset + newItems.length);
      })
      .catch(() => { if (myReq === reqRef.current && !append) setItems([]); })
      .finally(() => { if (myReq === reqRef.current) setLoading(false); });
  }, [endpoint]);

  React.useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setOffset(0); fetchPage(q.trim(), 0, false); }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, open, fetchPage]);

  const onScroll = (e) => {
    const el = e.target;
    if (!loading && hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      fetchPage(q.trim(), offset, true);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
        {Icon && <Icon size={11} weight="bold" className="text-gray-400" />}{label}
      </span>
      <div className="flex items-center gap-1.5">
        <input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white"
        />
        <button
          type="button"
          onClick={() => { const n = !open; setOpen(n); if (n && items.length === 0) fetchPage("", 0, false); }}
          className="shrink-0 border border-gray-300 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-700 hover:border-[#0020C2] hover:text-[#0020C2] inline-flex items-center gap-1"
        >
          Browse <CaretDown size={10} weight="bold" className={open ? "rotate-180" : ""} />
        </button>
      </div>
      {open && (
        <div className="border border-[#0020C2]/20 rounded-lg overflow-hidden">
          <input
            autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}…`}
            className="w-full px-2.5 py-1.5 text-xs outline-none border-b border-gray-100"
          />
          <div ref={listRef} onScroll={onScroll} className="max-h-44 overflow-y-auto">
            {items.map((it, i) => (
              <button
                type="button" key={`${it.name}-${i}`}
                onClick={() => { onChange(it.name); setOpen(false); setQ(""); }}
                className="w-full text-left px-2.5 py-1.5 hover:bg-[#0020C2]/5 border-b border-gray-50 last:border-0 flex items-center gap-2"
              >
                <span className="min-w-0 flex-1 truncate text-xs text-gray-800">{it.name}</span>
                {it.number && <span className="text-[10px] text-gray-400 font-mono shrink-0">#{it.number}</span>}
              </button>
            ))}
            {loading && (
              <div className="px-2.5 py-2 text-[11px] text-gray-400 flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" /> Loading…
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="px-2.5 py-2 text-[11px] text-gray-400">No matches{q.trim() ? ` for "${q.trim()}"` : ""}.</div>
            )}
            {!loading && hasMore && (
              <div className="px-2.5 py-1.5 text-[10px] text-gray-400 text-center">Scroll for more…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── GL account picker (per-supplier history, description search) ─────────── */
function GlPicker({ supplier, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const debounceRef = React.useRef(null);
  const reqRef = React.useRef(0);

  const fetchGl = React.useCallback((search) => {
    if (!supplier) return;
    const myReq = ++reqRef.current;
    setLoading(true);
    api.get("/erp-agents/invoice-create/gl-lov", { params: { supplier, search } })
      .then(({ data }) => { if (myReq === reqRef.current) setItems(data?.items || []); })
      .catch(() => { if (myReq === reqRef.current) setItems([]); })
      .finally(() => { if (myReq === reqRef.current) setLoading(false); });
  }, [supplier]);

  React.useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchGl(q.trim()), 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, open, fetchGl]);

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
        <Coins size={11} weight="bold" className="text-gray-400" /> GL account
      </span>
      <div className="flex items-center gap-1.5">
        <input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="101.00.…"
          className="flex-1 border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white font-mono"
        />
        <button
          type="button"
          onClick={() => { const n = !open; setOpen(n); if (n && items.length === 0) fetchGl(""); }}
          disabled={!supplier}
          className="shrink-0 border border-gray-300 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-700 hover:border-[#0020C2] hover:text-[#0020C2] disabled:opacity-50 inline-flex items-center gap-1"
        >
          Browse <CaretDown size={10} weight="bold" className={open ? "rotate-180" : ""} />
        </button>
      </div>
      {open && (
        <div className="border border-[#0020C2]/20 rounded-lg overflow-hidden">
          <input
            autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder='Search past GL by description…'
            className="w-full px-2.5 py-1.5 text-xs outline-none border-b border-gray-100"
          />
          <div className="max-h-44 overflow-y-auto">
            {loading && <div className="px-2.5 py-2 text-[11px] text-gray-400">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="px-2.5 py-2 text-[11px] text-gray-400">
                {q.trim() ? `No past GL matches "${q.trim()}".` : "No historical GL for this supplier — type one above."}
              </div>
            )}
            {!loading && items.map((it, i) => (
              <button
                type="button" key={`${it.gl}-${i}`}
                onClick={() => { onChange(it.gl); setOpen(false); setQ(""); }}
                className="w-full text-left px-2.5 py-1.5 hover:bg-[#0020C2]/5 border-b border-gray-50 last:border-0"
              >
                <div className="font-mono text-[11px] text-gray-900 break-all">{it.gl}</div>
                <div className="text-[10px] text-gray-500 flex items-center justify-between gap-2">
                  <span className="truncate">{it.description || "—"}</span>
                  <span className="shrink-0 text-gray-400">{it.use_count > 1 ? `used ${it.use_count}×` : ""}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Supplier site picker (dropdown from site-lov) ────────────────────────── */
function SitePicker({ supplier, businessUnit, value, onChange }) {
  const [sites, setSites] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!supplier) { setSites([]); return; }
    let alive = true;
    setLoading(true);
    api.get("/erp-agents/invoice-create/site-lov", { params: { supplier, procurement_bu: businessUnit || "" } })
      .then(({ data }) => { if (alive) setSites(data?.items || []); })
      .catch(() => { if (alive) setSites([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [supplier, businessUnit]);

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
        <Package size={11} weight="bold" className="text-gray-400" /> Supplier site
      </span>
      {loading ? (
        <div className="px-2.5 py-1.5 text-[11px] text-gray-400 border border-gray-200 rounded flex items-center gap-2">
          <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" /> Loading sites…
        </div>
      ) : sites.length > 0 ? (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white font-mono"
        >
          <option value="">Select a site…</option>
          {value && !sites.some((s) => s.site === value) && <option value={value}>{value} (recommended)</option>}
          {sites.map((s, i) => (
            <option key={`${s.site}-${i}`} value={s.site}>
              {s.site}{s.procurement_bu ? ` — ${s.procurement_bu}` : ""}{s.pay_site ? " (pay)" : ""}
            </option>
          ))}
        </select>
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. DUBAI"
          className="border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white font-mono"
        />
      )}
    </div>
  );
}

function Field({ icon: Icon, label, value, onChange, mono, type = "text", placeholder }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
        {Icon && <Icon size={11} weight="bold" className="text-gray-400" />}{label}
      </span>
      <input
        type={type} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white ${mono ? "font-mono" : ""}`}
      />
    </label>
  );
}

function MatchBadge({ type, matched, variance }) {
  const t = (type || "").toUpperCase();
  const ok = matched === true;
  const cls = ok ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-amber-200 text-amber-700 bg-amber-50";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${cls}`}>
      {ok ? <CheckCircle size={11} weight="fill" /> : <WarningCircle size={11} weight="fill" />}
      {t || "—"}{variance != null ? ` · var ${variance}` : ""}
    </span>
  );
}

function ReadKV({ label, value, mono }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-gray-400">{label}</span>
      <span className={`text-sm ${mono ? "font-mono" : ""} font-medium text-gray-900`}>{value ?? "—"}</span>
    </div>
  );
}

export default function PdfInvoiceReview({ open, sessionId, extracted, pdfUrl, onClose, onCreated }) {
  const isNonPo = !!extracted?.is_non_po;

  const [hdr, setHdr] = React.useState({});
  const [gl, setGl] = React.useState("");
  const [lines, setLines] = React.useState([]);
  const [creating, setCreating] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [taxCodes, setTaxCodes] = React.useState([]);   // [{code, rate, grosses_up, is_default}]

  // Whether the extracted invoice carries any tax (drives the Tax column).
  const hasTax = React.useMemo(
    () => !!extracted && ((extracted.tax_total || 0) > 0 || (extracted.invoice_lines || []).some((l) => (l.tax_rate || l.tax_amount))),
    [extracted]
  );

  React.useEffect(() => {
    if (!extracted) return;
    setErr("");
    setHdr({
      supplier_name: extracted.supplier_name || "",
      business_unit: extracted.business_unit || "",
      invoice_number: extracted.invoice_number || "",
      invoice_date: extracted.invoice_date || "",
      currency: extracted.currency || "AED",
      amount: extracted.amount ?? "",
      po_number: extracted.po_number || "",
      payment_terms: extracted.payment_terms || "",
      supplier_site: extracted.recommended_site || extracted.supplier_site || "",
    });
    setGl(extracted.recommended_gl || extracted.recommended_gl_account || "");
    const src = isNonPo ? (extracted.invoice_lines || []) : (extracted.po_lines || extracted.invoice_lines || []);
    setLines(src.map((l, i) => ({
      line_number: l.line_number ?? i + 1,
      item_number: l.item_number || "",
      description: l.description || "",
      quantity: l.quantity ?? l.invoice_qty ?? "",
      unit_price: l.unit_price ?? l.price ?? "",
      amount: l.amount ?? l.line_amount ?? l.invoice_taxable_amount ?? "",
      tax_rate: l.tax_rate ?? "",
      tax_amount: l.tax_amount ?? "",
      tax_code: "",  // filled once tax codes load (see effect below)
    })));
  }, [extracted, isNonPo]);

  // Load tax classifications for the supplier so the user can pick per line.
  React.useEffect(() => {
    if (!open || !extracted) return;
    const supplier = extracted.supplier_name || "";
    const bu = extracted.business_unit || "";
    api.get("/erp-agents/invoice-create/tax-lov", { params: { supplier, business_unit: bu } })
      .then(({ data }) => setTaxCodes(data?.items || []))
      .catch(() => setTaxCodes([]));
  }, [open, extracted]);

  // Match an extracted numeric rate to a tax classification code (closest rate).
  const codeForRate = React.useCallback((rate) => {
    if (!taxCodes.length) return "";
    const r = Number(rate) || 0;
    const exact = taxCodes.find((t) => Number(t.rate) === r);
    if (exact) return exact.code;
    // else the default (or standard) code
    const def = taxCodes.find((t) => t.is_default) || taxCodes[0];
    return def ? def.code : "";
  }, [taxCodes]);

  React.useEffect(() => {
    if (!taxCodes.length) return;
    setLines((prev) => prev.map((l) => l.tax_code ? l : { ...l, tax_code: codeForRate(l.tax_rate) }));
  }, [taxCodes, codeForRate]);

  if (!open || !extracted) return null;

  const setH = (k) => (v) => setHdr((d) => ({ ...d, [k]: v }));
  const setLine = (i, k, v) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((prev) => [...prev, { line_number: prev.length + 1, item_number: "", description: "", quantity: "", unit_price: "", amount: "" }]);
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const lineTotal = lines.reduce((s, l) => s + (Number(l.amount) || (Number(l.quantity) || 0) * (Number(l.unit_price) || 0)), 0);

  const createInvoice = async () => {
    setCreating(true); setErr("");
    try {
      const payload = {
        session_id: sessionId,
        message: "CONFIRM_PDF_INVOICE " + JSON.stringify({
          header: { ...hdr, recommended_gl_account: gl },
          lines: lines.map((l) => {
            const t = taxCodes.find((c) => c.code === l.tax_code);
            return {
              line_number: l.line_number, item_number: l.item_number, description: l.description,
              quantity: l.quantity === "" ? null : Number(l.quantity),
              unit_price: l.unit_price === "" ? null : Number(l.unit_price),
              amount: l.amount === "" ? null : Number(l.amount),
              tax_code: l.tax_code || null,
              tax_rate: t ? t.rate : (l.tax_rate === "" ? null : Number(l.tax_rate)),
              tax_grosses_up: t ? !!t.grosses_up : undefined,
            };
          }),
        }),
      };
      const { data } = await api.post("/erp-agents/invoice-create/chat", payload);
      const status = (data?.status || "").toLowerCase();
      if (["ok", "success", "completed"].includes(status) || data?.invoice_id) onCreated?.(data);
      else setErr(data?.reply || data?.response || data?.message || "Create did not complete — check the chat.");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Create failed.");
    } finally { setCreating(false); }
  };

  return (
    <div className="absolute inset-0 z-[80] flex bg-white overflow-hidden" data-testid="pdf-invoice-review-overlay">
      {/* Left: editable review form (~70%) */}
      <div className="relative flex-1 min-w-0 bg-white h-full flex flex-col overflow-hidden" data-testid="pdf-invoice-review">
        <div className="bg-white border-b border-gray-200 px-6 pt-4 pb-4 shrink-0">
          <div className="flex items-start justify-between w-full">
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-1 flex items-center gap-1.5">
                <Sparkle size={11} weight="fill" /> Extracted from PDF · review &amp; create
              </div>
              <h3 className="text-xl font-black tracking-tight">{hdr.invoice_number || "New invoice"}</h3>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                {isNonPo ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-violet-200 text-violet-700 bg-violet-50">Non-PO</span>
                ) : (
                  <MatchBadge type={extracted.match_type} matched={extracted.matched} variance={extracted.variance} />
                )}
                {!extracted.supplier_exists && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-amber-200 text-amber-700 bg-amber-50">
                    <WarningCircle size={11} weight="fill" /> Supplier not found
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} data-testid="pdf-review-close"><X size={18} /></button>
          </div>
        </div>

        <div className="p-6 space-y-6 w-full flex-1 overflow-y-auto">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-2">Invoice header</div>
            <div className="grid grid-cols-2 gap-3">
              <LovPicker label="Supplier" icon={User} value={hdr.supplier_name} onChange={setH("supplier_name")}
                endpoint="/erp-agents/invoice-create/supplier-lov" placeholder="Supplier name" />
              <LovPicker label="Business unit" icon={Package} value={hdr.business_unit} onChange={setH("business_unit")}
                endpoint="/erp-agents/invoice-create/business-unit-lov" placeholder="Business unit" />
              <Field icon={Hash} label="Invoice number" value={hdr.invoice_number} onChange={setH("invoice_number")} mono />
              <Field icon={CalendarBlank} label="Invoice date" value={hdr.invoice_date} onChange={setH("invoice_date")} mono placeholder="YYYY-MM-DD" />
              <Field icon={CurrencyDollar} label={`Amount (${hdr.currency || ""})`} value={hdr.amount} onChange={setH("amount")} type="number" mono />
              <Field label="Currency" value={hdr.currency} onChange={setH("currency")} mono />
              {!isNonPo && <Field icon={LinkIcon} label="PO number" value={hdr.po_number} onChange={setH("po_number")} mono />}
              <Field label="Payment terms" value={hdr.payment_terms} onChange={setH("payment_terms")} />
            </div>
          </div>

          {isNonPo && (
            <div className="border border-[#0020C2]/25 bg-blue-50/40 p-4">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-3 flex items-center gap-1">
                <Sparkle size={11} weight="fill" /> GL account &amp; site — recommended, edit if needed
              </div>
              <div className="grid grid-cols-2 gap-3">
                <GlPicker supplier={hdr.supplier_name} value={gl} onChange={setGl} />
                <SitePicker supplier={hdr.supplier_name} businessUnit={hdr.business_unit} value={hdr.supplier_site} onChange={setH("supplier_site")} />
              </div>
              {extracted.gl_reason && <p className="mt-2 text-[10px] text-gray-500">{extracted.gl_reason}</p>}
            </div>
          )}

          {!isNonPo && (
            <div className="border border-gray-200 bg-gray-50/60 p-4">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-2">Purchase order match</div>
              <div className="grid grid-cols-3 gap-x-6 gap-y-2">
                <ReadKV label="PO total" value={money(extracted.po_total, hdr.currency)} />
                <ReadKV label="Match type" value={extracted.match_type} />
                <ReadKV label="Matched" value={extracted.matched ? "Yes" : "No"} />
                <ReadKV label="Variance" value={extracted.variance} />
                <ReadKV label="PO status" value={extracted.po_status} />
                <ReadKV label="Receipts" value={extracted.receipt_exists ? "Found" : "—"} />
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2]">Line items · {lines.length}</div>
              <span className="text-[11px] font-mono text-gray-500">{money(lineTotal, hdr.currency)}</span>
            </div>
            <div className="border border-gray-200 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500">
                  <tr className="text-left">
                    <th className="px-2 py-1.5 font-bold">#</th>
                    <th className="px-2 py-1.5 font-bold">Item</th>
                    <th className="px-2 py-1.5 font-bold">Description</th>
                    <th className="px-2 py-1.5 font-bold text-right">Qty</th>
                    <th className="px-2 py-1.5 font-bold text-right">Unit price</th>
                    <th className="px-2 py-1.5 font-bold text-right">Amount</th>
                    {hasTax && <th className="px-2 py-1.5 font-bold">Tax</th>}
                    <th className="px-2 py-1.5 font-bold text-center">·</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-2 py-1 w-8">{l.line_number}</td>
                      <td className="px-2 py-1">
                        <input value={l.item_number} onChange={(e) => setLine(i, "item_number", e.target.value)}
                          className="w-24 border border-transparent hover:border-gray-200 focus:border-[#0020C2] outline-none px-1 py-0.5 font-mono text-[11px]" />
                      </td>
                      <td className="px-2 py-1">
                        <input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)}
                          className="w-full min-w-[160px] border border-transparent hover:border-gray-200 focus:border-[#0020C2] outline-none px-1 py-0.5" />
                      </td>
                      <td className="px-2 py-1 text-right">
                        <input value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} inputMode="decimal"
                          className="w-16 text-right border border-transparent hover:border-gray-200 focus:border-[#0020C2] outline-none px-1 py-0.5" />
                      </td>
                      <td className="px-2 py-1 text-right">
                        <input value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} inputMode="decimal"
                          className="w-20 text-right border border-transparent hover:border-gray-200 focus:border-[#0020C2] outline-none px-1 py-0.5" />
                      </td>
                      <td className="px-2 py-1 text-right">
                        <input value={l.amount} onChange={(e) => setLine(i, "amount", e.target.value)} inputMode="decimal"
                          className="w-24 text-right border border-transparent hover:border-gray-200 focus:border-[#0020C2] outline-none px-1 py-0.5 font-medium" />
                      </td>
                      {hasTax && (
                        <td className="px-2 py-1">
                          {taxCodes.length > 0 ? (
                            <select
                              value={l.tax_code || ""}
                              onChange={(e) => setLine(i, "tax_code", e.target.value)}
                              className="min-w-[130px] border border-gray-200 focus:border-[#0020C2] outline-none px-1 py-0.5 text-[11px] bg-white"
                            >
                              <option value="">— none —</option>
                              {taxCodes.map((t) => (
                                <option key={t.code} value={t.code}>
                                  {t.code} ({t.rate}%)
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-[11px] text-gray-500">
                              {l.tax_rate ? `${l.tax_rate}%` : "—"}
                            </span>
                          )}
                          {l.tax_amount ? (
                            <div className="text-[9px] text-gray-400 mt-0.5">tax {Number(l.tax_amount).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                          ) : null}
                        </td>
                      )}
                      <td className="px-2 py-1 text-center">
                        <button onClick={() => removeLine(i)} className="text-[#DC2626] p-1" title="Remove line">
                          <Trash size={12} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={addLine} className="mt-2 text-[11px] font-bold text-[#0020C2] hover:underline flex items-center gap-1">
              <Plus size={12} weight="bold" /> Add line
            </button>
          </div>

          {extracted.ocr_text && (
            <details>
              <summary className="text-[10px] font-bold uppercase tracking-wider text-gray-500 cursor-pointer hover:text-gray-700">Extracted text ↓</summary>
              <pre className="text-[10px] font-mono bg-gray-50 border border-gray-200 p-2 mt-1 overflow-x-auto max-h-48 whitespace-pre-wrap">{extracted.ocr_text}</pre>
            </details>
          )}

          {err && (
            <div className="border border-[#DC2626] bg-red-50 px-3 py-2 text-xs text-[#DC2626] flex items-start gap-2">
              <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" />
              <span className="font-bold">{err}</span>
            </div>
          )}
        </div>

        <div className="bg-white border-t border-gray-200 p-4 flex gap-3 w-full shrink-0">
          <button onClick={createInvoice} disabled={creating} data-testid="pdf-review-create"
            className="flex-1 bg-emerald-600 text-white py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {creating ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <ArrowRight size={13} weight="bold" />}
            {creating ? "Creating…" : "Create invoice in Oracle"}
          </button>
          <button onClick={onClose} disabled={creating}
            className="border border-gray-300 text-gray-600 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
        </div>
      </div>

      {/* Right: uploaded PDF preview (30%) */}
      <div className="hidden md:flex md:w-[30%] shrink-0 border-l border-gray-200 bg-gray-100 flex-col overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-200 bg-white flex items-center gap-2 shrink-0">
          <Receipt size={13} weight="bold" className="text-[#4A2C17]" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Uploaded invoice</span>
        </div>
        <div className="flex-1 overflow-hidden">
          {pdfUrl ? (
            <iframe title="Invoice PDF" src={pdfUrl + "#toolbar=1&view=FitH"} className="w-full h-full border-0" />
          ) : (
            <div className="h-full flex items-center justify-center text-xs text-gray-400 px-3 text-center">PDF preview unavailable</div>
          )}
        </div>
      </div>
    </div>
  );
}